import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EstQtrMaintenanceService } from './est-qtr-maintenance.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-est-qtr-maintenance',
    templateUrl: './est-qtr-maintenance.component.html',
    styleUrls: ['./est-qtr-maintenance.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EstQtrMaintenanceComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isLoading = false;

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  showFormLoader = true;
  showBackdrop = false;
  lockedqtrlist: any[] = [];
  quarterList: any[] = [];
  showLockConfirmModal = false;
  lockConfirmConfig: ConfirmationModalConfig = {
    title: 'Lock',
    message: 'Are you sure you want to lock this quarter?',
    iconClass: 'fa fa-lock',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true
  };
  private pendingLockItem: any = null;
  private pendingLockIndex = -1;

  rollover: any = {
    tax_year: '',
    out_tax_year: '',
    quarter: '',
    locked_status: 0
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private estQtrService: EstQtrMaintenanceService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.onTabActivation();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
    }));

    this.onInitialRender();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);

    this.cdr.detectChanges();
  }

  private onInitialRender(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    if (activeScreen?.data && Array.isArray(activeScreen.data)) {
      this.ensureQuarterListForDisplay(activeScreen.data);
      this.showBackdrop = false;
      this.showFormLoader = false;
    } else {
      // Legacy parity: wait for Filter click.
      this.showBackdrop = true;
      this.showFormLoader = false;
    }
    this.cdr.detectChanges();
  }

  private onTabActivation(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    if (!this.isEstQtrMaintenanceScreen(activeScreen)) {
      return;
    }

    // Filter button flow triggers loading_data=true before setTabActive.
    if (activeScreen?.loading_data === true) {
      this.loadAllDataAfterFilterClick();
      return;
    }

    if (activeScreen?.data && Array.isArray(activeScreen.data)) {
      this.ensureQuarterListForDisplay(activeScreen.data);
      this.showBackdrop = false;
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private isEstQtrMaintenanceScreen(activeScreen: any): boolean {
    const link = String(activeScreen?.link || '').toLowerCase();
    return link.includes('est-qtr-maintenance')
      || link.includes('est-quarter-maintenance')
      || link.includes('est-qtr-maint')
      || link.includes('estqtrmaintenance');
  }

  private buildCommonParams(): any {
    return {
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '',
      jcd_key: this.rowData?.JCD_KEY || this.globalService?.jcd_key || 250,
      out_year: this.filterParams?.out_year || '',
      workflow: this.filterParams?.wf_name || ''
    };
  }

  private loadAllDataAfterFilterClick(): void {
    if (this.isLoading || !this.GENERAL_CONFIG?.base_url) {
      return;
    }
    this.isLoading = true;
    this.showFormLoader = true;
    this.showBackdrop = false;
    this.cdr.detectChanges();

    const params = this.buildCommonParams();

    // Legacy order: 31590 (locked) then 31592 (quarter list for mapping).
    this.estQtrService.getMaintenanceData(this.GENERAL_CONFIG.base_url, params, 31590).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (lockedResp: any) => {
        const rows = Array.isArray(lockedResp?.jsonObject) ? lockedResp.jsonObject : [];
        this.estQtrService.getQuarterList(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (quarterResp: any) => {
            this.quarterList = this.normalizeQuarterList(quarterResp?.jsonObject);
            this.hydrateFromRows(rows);
            const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
            activeScreen.data = rows;
            activeScreen.loading_data = false;
            this.showFormLoader = false;
            this.isLoading = false;
            this.cdr.detectChanges();
          },
          (quarterErr: any) => {
            console.error('Error loading quarter list:', quarterErr);
            this.finishLoadWithError();
          }
        );
      },
      (lockedErr: any) => {
        console.error('Error loading maintenance data:', lockedErr);
        this.finishLoadWithError();
      }
    );
  }

  private hydrateFromRows(rows: any[]): void {
    this.lockedqtrlist = rows.map((row: any) => {
      const quarterValue = row?.qtr ?? row?.QTR ?? row?.quarter ?? row?.QUARTER ?? '';
      const quarterObj = this.quarterList.find((q: any) =>
        String(q?.VALUE ?? q?.value) === String(quarterValue)
      );
      const lockState = row?.locked_status ?? row?.LOCKED_STATUS ?? row?.lock_status ?? row?.lock_y_n ?? row?.LOCK_Y_N ?? 0;
      return {
        tax_year: row?.tax_year ?? row?.TAX_YEAR ?? '',
        out_tax_year: row?.out_year ?? row?.out_tax_year ?? row?.OUT_YEAR ?? row?.OUT_TAX_YEAR ?? '',
        quarter: quarterObj || quarterValue,
        locked_status: lockState === 'Y' || lockState === 1 || String(lockState).toUpperCase() === 'Y' ? 1 : 0
      };
    });
  }

  private normalizeQuarterList(list: any): any[] {
    if (!Array.isArray(list)) {
      return [];
    }
    return list.map((item: any) => {
      const value = String(item?.VALUE ?? item?.value ?? '');
      const label = item?.LABEL ?? item?.label;
      if (value === '0' && (label === undefined || label === null || String(label).trim() === '')) {
        return { ...item, LABEL: 'Undefined' };
      }
      return item;
    });
  }

  private ensureQuarterListForDisplay(rows: any[]): void {
    if (!Array.isArray(rows)) {
      this.lockedqtrlist = [];
      return;
    }

    if (this.quarterList.length > 0) {
      this.hydrateFromRows(rows);
      return;
    }

    if (!this.GENERAL_CONFIG?.base_url) {
      this.hydrateFromRows(rows);
      return;
    }

    const params = this.buildCommonParams();
    this.estQtrService.getQuarterList(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (quarterResp: any) => {
        this.quarterList = this.normalizeQuarterList(quarterResp?.jsonObject);
        this.hydrateFromRows(rows);
        this.cdr.detectChanges();
      },
      () => {
        this.hydrateFromRows(rows);
        this.cdr.detectChanges();
      }
    );
  }

  compareQuarterByValue = (a: any, b: any): boolean => {
    const av = String(a?.VALUE ?? a?.value ?? a ?? '');
    const bv = String(b?.VALUE ?? b?.value ?? b ?? '');
    return av === bv;
  };

  private finishLoadWithError(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    activeScreen.loading_data = false;
    this.showFormLoader = false;
    this.isLoading = false;
    this.gtwSnackbarService.openSnackBar('Error loading EST quarter maintenance data', 'gtw-snackbar--red');
    this.cdr.detectChanges();
  }

  addMaintenance() {
    this.lockedqtrlist.push({
      tax_year: this.filterParams?.tax_year || '',
      out_tax_year: this.filterParams?.out_year || '',
      quarter: null,
      locked_status: 0
    });
    this.cdr.detectChanges();
  }

  deleteLockedRow(index: number) {
    this.lockedqtrlist.splice(index, 1);
    this.cdr.detectChanges();
  }

  onLockToggle(event: Event, item: any, index: number): void {
    const checked = !!(event.target as HTMLInputElement)?.checked;
    if (!checked) {
      item.locked_status = 0;
      this.cdr.detectChanges();
      return;
    }

    this.pendingLockItem = item;
    this.pendingLockIndex = index;
    item.locked_status = 1;
    this.showLockConfirmModal = true;
    this.cdr.detectChanges();
  }

  onConfirmLock(): void {
    if (!this.pendingLockItem || this.pendingLockIndex < 0) {
      this.showLockConfirmModal = false;
      return;
    }
    this.showLockConfirmModal = false;
    this.saveLock(this.pendingLockItem, this.pendingLockIndex);
    this.pendingLockItem = null;
    this.pendingLockIndex = -1;
  }

  onCancelLock(): void {
    if (this.pendingLockItem) {
      this.pendingLockItem.locked_status = 0;
    }
    this.showLockConfirmModal = false;
    this.pendingLockItem = null;
    this.pendingLockIndex = -1;
    this.cdr.detectChanges();
  }

  saveLock(item: any, index: number) {
    const quarterValue = item?.quarter?.VALUE ?? item?.quarter;
    const params = {
      tax_year: item?.tax_year || this.filterParams?.tax_year || this.globalService?.tax_year,
      scenario: this.filterParams?.scenario || this.globalService?.scenario,
      jcd_key: this.rowData?.JCD_KEY || this.globalService?.jcd_key || 250,
      qtr: quarterValue,
      out_year: item?.out_tax_year || this.filterParams?.out_year,
      workflow: this.filterParams?.wf_name || '',
      lock_y_n: item?.locked_status === 1 ? 'Y' : 'N'
    };

    this.estQtrService.saveLock(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1' && data?.errorMessage === 'null') {
          this.gtwSnackbarService.openSnackBar('Quarter lock saved successfully', 'gtw-snackbar--green');
          this.loadAllDataAfterFilterClick();
          this.cdr.detectChanges();
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Error saving quarter lock', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.gtwSnackbarService.openSnackBar('Error saving quarter lock', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
